import { useEffect, useState } from 'react';
import { copyToClipboard } from '../lib/clipboard';
import { t, tp } from '../lib/i18n';
import SharePanel from '../components/SharePanel';
import type { ParticipantCampaign, ParticipantOverview } from '../lib/types';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

const KNOWN_STATUSES = ['draft', 'active', 'paused', 'ended'];
function statusLabel(status: string): string {
  return KNOWN_STATUSES.includes(status) ? t(`status.${status}`) : status;
}

// The portal is accessible without login — the participant endpoint is public
// and identifies the user via the ?uid query parameter. We deliberately use
// native fetch here (no auth headers) because the visitor may not be signed
// into the marketplace.
async function publicFetch<T>(path: string): Promise<T> {
  const res = await fetch(`${BACKEND_URL}${path}`);
  const data = (await res.json()) as { error?: string } & T;
  if (!res.ok) throw new Error(data.error ?? `http_${res.status}`);
  return data as T;
}

export default function Portal() {
  const params = new URLSearchParams(window.location.search);
  const uid = params.get('uid');
  const initialCampaignId = params.get('campaign');

  const [overview, setOverview] = useState<ParticipantOverview | null>(null);
  const [active, setActive] = useState<string | null>(initialCampaignId);
  const [detail, setDetail] = useState<ParticipantCampaign | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setError(t('portal.noUid'));
      return;
    }
    publicFetch<ParticipantOverview>(`/api/participant/${uid}/overview`)
      .then((o) => {
        setOverview(o);
        if (!active && o.campaigns.length > 0) setActive(o.campaigns[0].campaignId);
      })
      .catch((e) => setError((e as Error).message));
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!uid || !active) return;
    publicFetch<ParticipantCampaign>(`/api/participant/${uid}/campaign/${active}`)
      .then(setDetail)
      .catch((e) => setError((e as Error).message));
  }, [uid, active]);

  if (error) {
    return (
      <main style={{ padding: '1.5rem' }}>
        <div className="topbar"><div className="brand">Viral<span>Loop</span></div></div>
        <div className="error" style={{ marginTop: '1rem' }}>{error}</div>
      </main>
    );
  }

  if (!overview) {
    return (
      <main style={{ padding: '1.5rem' }}>
        <div className="muted">{t('common.loading')}</div>
      </main>
    );
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">Viral<span>Loop</span></div>
        <div className="user">
          {overview.displayName ? (
            <>{t('portal.welcome')} <strong>{overview.displayName}</strong></>
          ) : null}
          {' · '}
          <span className="badge">
            {tp('portal.rewardsWonBadge', { n: overview.totalRewardsWon })}
          </span>
        </div>
      </header>

      <main>
        {overview.campaigns.length === 0 && (
          <div className="card muted">{t('portal.notRegistered')}</div>
        )}

        {overview.campaigns.length > 0 && (
          <>
            <div className="card" style={{ marginBottom: '1rem' }}>
              <h3 style={{ marginTop: 0 }}>{t('portal.myCampaigns')}</h3>
              <div className="grid-2">
                {overview.campaigns.map((c) => (
                  <button
                    key={c.campaignId}
                    className={active === c.campaignId ? '' : 'secondary'}
                    onClick={() => setActive(c.campaignId)}
                    style={{ textAlign: 'left', padding: '0.75rem' }}
                  >
                    <div style={{ fontWeight: 700 }}>{c.campaignName}</div>
                    <div className="small">
                      <span className={`badge ${c.status}`}>{statusLabel(c.status)}</span>
                      {' · '}
                      {c.leaderboard.position
                        ? tp('portal.cardSummary', {
                            count: c.referralCount,
                            pos: c.leaderboard.position,
                            total: c.leaderboard.totalParticipants,
                          })
                        : tp('portal.cardSummaryNoPosition', { count: c.referralCount })}
                    </div>
                    {c.nextMilestone && (
                      <div className="small muted" style={{ marginTop: '0.25rem' }}>
                        {tp('portal.nextMilestoneCard', {
                          n: c.nextMilestone.remaining,
                          label: c.nextMilestone.rewardLabel,
                        })}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {detail && <CampaignDetailPanel detail={detail} />}
          </>
        )}
      </main>
    </div>
  );
}

function CampaignDetailPanel({ detail }: { detail: ParticipantCampaign }) {
  const [copied, setCopied] = useState(false);

  async function copyCode() {
    await copyToClipboard(detail.referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const days = Math.floor(detail.campaign.secondsRemaining / 86400);
  const hours = Math.floor((detail.campaign.secondsRemaining % 86400) / 3600);

  // next milestone progress
  const nextMilestone = detail.rewards.milestones.find((m) => !m.reached);
  const progressPct = nextMilestone && nextMilestone.triggerValue
    ? Math.min(100, ((detail.referralCount / nextMilestone.triggerValue) * 100))
    : 100;

  return (
    <div>
      <div className="flex-between" style={{ marginBottom: '0.75rem' }}>
        <h2 style={{ margin: 0 }}>{detail.campaignName}</h2>
        {detail.campaign.status === 'active' && (
          <span className="muted small">{tp('portal.endsIn', { d: days, h: hours })}</span>
        )}
      </div>

      <div className="stat-grid">
        <div className="card">
          <div className="stat-label">{t('portal.yourReferrals')}</div>
          <div className="stat-value">{detail.referralCount}</div>
        </div>
        <div className="card">
          <div className="stat-label">{t('portal.leaderboardCard')}</div>
          <div className="stat-value">
            {detail.leaderboard.position ? `#${detail.leaderboard.position}` : '—'}
          </div>
          <div className="muted small">
            {detail.leaderboard.percentile ??
              tp('portal.outOfTotal', { n: detail.leaderboard.totalParticipants })}
          </div>
        </div>
        <div className="card">
          <div className="stat-label">{t('portal.rewardsWonStat')}</div>
          <div className="stat-value">{detail.rewards.won.length}</div>
        </div>
        <div className="card">
          <div className="stat-label">{t('portal.yourCode')}</div>
          <div className="stat-value" style={{ fontFamily: 'monospace' }}>{detail.referralCode}</div>
          <button onClick={copyCode} style={{ marginTop: '0.25rem' }}>
            {copied ? t('portal.copied') : t('portal.copyCode')}
          </button>
        </div>
      </div>

      {nextMilestone && nextMilestone.triggerValue && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="flex-between">
            <strong>{tp('portal.nextMilestone', { label: nextMilestone.rewardLabel })}</strong>
            <span className="small muted">
              {tp('portal.referralsRemaining', { n: nextMilestone.remaining })}
            </span>
          </div>
          <div className="progress" style={{ marginTop: '0.5rem' }}>
            <div style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      <div className="grid-2" style={{ marginBottom: '1rem' }}>
        <div className="card">
          <h3 style={{ marginTop: 0 }}>{t('portal.luckyDrawTitle')}</h3>
          {!detail.rewards.luckyDraw.triggered && (
            <div className="muted">{t('portal.noLuckyDraw')}</div>
          )}
          {detail.rewards.luckyDraw.triggered && (
            <div className={detail.rewards.luckyDraw.won ? 'success-banner' : 'muted'}>
              {detail.rewards.luckyDraw.won
                ? t('portal.luckyDrawWon')
                : t('portal.luckyDrawLost')}
            </div>
          )}
        </div>

        <div className="card">
          <h3 style={{ marginTop: 0 }}>{t('portal.milestonesTitle')}</h3>
          {detail.rewards.milestones.length === 0 ? (
            <div className="muted">{t('portal.noMilestones')}</div>
          ) : (
            <ul style={{ paddingLeft: '1.2rem', margin: 0 }}>
              {detail.rewards.milestones.map((m, i) => (
                <li key={i} style={{ color: m.reached ? '#34d399' : 'var(--text-dim)' }}>
                  {m.reached ? '✓' : '○'}{' '}
                  {tp('portal.milestoneRow', {
                    trigger: m.triggerValue ?? '',
                    label: m.rewardLabel,
                  })}
                  {!m.reached && (
                    <span className="small">
                      {' '}{tp('portal.milestoneRemaining', { n: m.remaining })}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {detail.rewards.topN.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>{t('portal.topNTitle')}</h3>
          <table>
            <thead>
              <tr>
                <th>{t('portal.topN.col.reward')}</th>
                <th>{t('portal.topN.col.period')}</th>
                <th style={{ textAlign: 'right' }}>{t('portal.topN.col.position')}</th>
                <th>{t('portal.topN.col.status')}</th>
              </tr>
            </thead>
            <tbody>
              {detail.rewards.topN.map((row, i) => (
                <tr key={i}>
                  <td>{row.rewardLabel}</td>
                  <td className="small">
                    {row.currentPeriod ?? t('portal.topN.finalStandings')}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {row.currentPosition ? `#${row.currentPosition}` : '—'}
                  </td>
                  <td>
                    {row.currentlyWinning ? (
                      <span className="badge active">{t('portal.topN.onCourse')}</span>
                    ) : (
                      <span className="badge">
                        {tp('portal.topN.outsideTopN', { n: row.triggerValue ?? '?' })}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>{t('portal.top10')}</h3>
        <table>
          <thead>
            <tr>
              <th>{t('portal.col.rank')}</th>
              <th>{t('portal.col.name')}</th>
              <th style={{ textAlign: 'right' }}>{t('portal.col.referrals')}</th>
            </tr>
          </thead>
          <tbody>
            {detail.leaderboard.top10.map((r) => (
              <tr key={r.rank} className={r.isMe ? 'me' : ''}>
                <td>{r.rank}</td>
                <td>
                  {r.isMe ? (
                    <strong>{r.displayName} ({t('portal.you')})</strong>
                  ) : (
                    r.displayName
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>{r.referralCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SharePanel
        links={detail.referralLinks}
        shareTexts={detail.shareTexts}
        referralCode={detail.referralCode}
      />
    </div>
  );
}
